import React, { useEffect } from 'react';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomFilter, { FilterValue, IFilter } from 'components/CustomFilter/CustomFilter';
import Table, { ISelectPanel } from 'components/CustomTable/CustomTable';
import TableHeader, { IButton } from 'components/TableHeader/TableHeader';
import { HeadingMBold, HeadingSBold, BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/styles';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/common/accountFilterTableStyle';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import Button from 'components/CustomButtons/Button';
import { IDropdownItem } from 'components/Dropdown/Dropdown';
import classNames from 'classnames';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { GridSize } from '@material-ui/core';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDropdown {
	action: string;
	menu: React.ReactElement | string;
	items: IDropdownItem[];
}

export interface ISelectedAccount {
	name: string;
	id: string | null;
}

interface IAccountFilterTableProps {
	accounts: { accountName: string; accountId: string }[];
	setAccount: React.Dispatch<React.SetStateAction<ISelectedAccount | null>>;
	selectedAccount: ISelectedAccount | null;
	filterHeader?: string;
	filtersValue?: FilterValue;
	filters: IFilter[];
	onFilterChange: (name: string, value: any[]) => void;
	data: any[];
	onSelect?: React.Dispatch<React.SetStateAction<never[]>>;
	selectPanel: ISelectPanel;
	selected?: number[];
	columns: Array<any>;
	setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
	searchInputValue: string;
	searchPlaceHolder?: string;
	searchType?: string;
	exportToXLS?: () => void;
	exportToCSV?: () => void;
	buttons?: IButton | IButton[];
	loading: boolean;
	tableLoading?: boolean;
	dropdown?: IDropdown;
	showTableAdditionalElement?: boolean;
	tableComment?: string;
	tableAdditionalElement?: React.ReactElement;
	onInfoButtonClick?: () => void;
	withoutCheckbox?: boolean;
	alignColumnsRight?: number[];
	additionalSelect?: React.ReactElement;
	onPaggingChange?: any;
	pageSizeOptions?: number[];
	onPageSizeChange?: any;
	tableAdditionalButton?: React.ReactElement;
	xs?: GridSize;
	sm?: GridSize;
	lg?: GridSize;
	hideOrgFilter?: boolean;
	showPaginationBottom?: boolean;
	withBackground?: boolean;
	alignColumnsCenter?: number[];
	defaultPageSize?: number;
	pageSize?: number;
	pages?: number;
	page?: number;
	totalElements?: number;
	enabledCheckoxes?: any[];
	showPaginationTop?: boolean;
}

const AccountFilterTable = (props: IAccountFilterTableProps) => {
	const {
		accounts,
		setAccount,
		selectedAccount,
		filterHeader,
		filtersValue = {},
		onFilterChange,
		filters,
		setSearchInputValue,
		searchInputValue,
		searchPlaceHolder,
		searchType,
		exportToXLS,
		exportToCSV,
		data,
		columns,
		selected,
		onSelect,
		selectPanel,
		loading,
		tableLoading,
		buttons,
		dropdown,
		tableAdditionalElement,
		onInfoButtonClick,
		showTableAdditionalElement,
		tableComment,
		withoutCheckbox,
		alignColumnsRight,
		additionalSelect = <></>,
		onPaggingChange,
		pageSizeOptions,
		onPageSizeChange,
		tableAdditionalButton,
		showPaginationTop = true,
		xs,
		sm,
		lg,
		hideOrgFilter,
		showPaginationBottom,
		alignColumnsCenter,
		defaultPageSize,
		pageSize,
		pages,
		page,
		totalElements,
		enabledCheckoxes,
	} = props;

	const classes = useStyles();
	const allAccountsOption = { accountName: translate('all.organizations'), accountId: null };
	const availableAccounts = accounts.length === 1 ? accounts : [allAccountsOption, ...accounts];
	const isEnterprise = availableAccounts.length > 1;

	const selectWrapperClasses = classNames({
		[classes.alternativeSelectWrapper]: true,
		[classes.billingsWrapper]: true,
	});

	useEffect(() => {
		if (accounts.length === 1) {
			setAccount({
				name: accounts[0].accountName,
				id: accounts[0].accountId,
			});
		}
		// eslint-disable-next-line
	}, [accounts]);

	const handleClick = (account: ISelectedAccount) => {
		if (selectedAccount && selectedAccount.id === account.id) {
			setAccount({
				name: availableAccounts[0].accountName,
				id: availableAccounts[0].accountId,
			});
		} else {
			setAccount(account);
		}
	};

	const items = React.useMemo(() => {
		return availableAccounts.map((account) => ({
			name: account.accountName,
			id: account.accountId,
		}));
	}, [availableAccounts]);

	const getSelectedItem = (account: ISelectedAccount | null) => {
		if (account && account.id) {
			return [account.id];
		} else {
			return [availableAccounts[0].accountId];
		}
	};

	return (
		<ConditionalRender
			show={!loading}
			onTrue={() => (
				<GridContainer justify="flex-start">
					<>
						{isEnterprise && !hideOrgFilter && (
							<div style={{ width: '100%' }}>
								<GridItem xs={12} sm={7} lg={4} className={classes.alternativeSelectWrapper}>
									<AlternativeSelect
										type="md"
										id="organization-select"
										value={
											<div className={classes.selectedItem}>
												<CustomAvatar
													accountId={selectedAccount?.id as string}
													alt="Company Logo"
													placeholderIcon={<Business />}
													customClasses={classes.selectedItemIcon}
												/>
												<HeadingSBold>
													{(selectedAccount && selectedAccount.name) ||
														availableAccounts[0].accountName}
												</HeadingSBold>
											</div>
										}
										onClick={(account) => handleClick(account)}
										items={items as Array<string | IAlternativeSelectItem>}
										customIcons
										selectedItems={getSelectedItem(selectedAccount)}
									/>
								</GridItem>
							</div>
						)}
						<GridItem xs={12}>
							<div className={hideOrgFilter ? '' : classes.headerWrapper}>
								{filterHeader && (
									<h1 className={classes.heading}>
										<HeadingMBold color="secondary-light" disableMargin asSpan>
											{filterHeader}
										</HeadingMBold>
									</h1>
								)}
								{!!onInfoButtonClick && (
									<Button justIcon round color="primary-light" onClick={onInfoButtonClick}>
										?
									</Button>
								)}
							</div>

							<div style={{ width: '100%' }}>
								<GridItem xs={12} sm={6} lg={6} className={selectWrapperClasses}>
									{additionalSelect}
								</GridItem>
							</div>

							<ConditionalRender
								show={!!tableAdditionalButton}
								onTrue={() => (
									<GridContainer justify="flex-start" className={classes.wrapper}>
										<GridItem xs={12} sm={12} lg={6}>
											<CustomFilter
												onChange={onFilterChange}
												value={filtersValue}
												filters={filters}
												xs={12}
												sm={6}
												lg={6}
											/>
										</GridItem>
										<GridItem xs={12} sm={12} lg={6} justify="flex-end">
											{tableAdditionalButton}
										</GridItem>
									</GridContainer>
								)}
								onFalse={() => (
									<CustomFilter
										onChange={onFilterChange}
										value={filtersValue}
										filters={filters}
										xs={xs}
										sm={sm}
										lg={lg}
									/>
								)}
							/>

							<ConditionalRender
								show={!!tableComment}
								onTrue={() => <BodyS color="disabled-light">{tableComment}</BodyS>}
							/>
							<TableHeader
								setSearchInputValue={setSearchInputValue}
								searchInputValue={searchInputValue}
								searchPlaceHolder={searchPlaceHolder}
								searchType={searchType}
								exportToXLS={exportToXLS}
								exportToCSV={exportToCSV}
								buttons={buttons}
								dropdown={dropdown}
								withBackground
							/>
							<div className={classes.tableContainer}>
								<Table
									total={totalElements || data.length}
									withHeader
									withCheckbox={!withoutCheckbox}
									onSelect={onSelect}
									selectPanel={selectPanel}
									selected={selected}
									alignColumnsRight={alignColumnsRight}
									showPaginationTop={showPaginationTop}
									showPaginationBottom={showPaginationBottom}
									onPaggingChange={onPaggingChange}
									onPageSizeChange={onPageSizeChange}
									pageSize={pageSize}
									pages={pages}
									page={page}
									enabledCheckoxes={enabledCheckoxes}
									tableProps={{
										data,
										columns,
										defaultPageSize: defaultPageSize || 10,
										pageSizeOptions,
										loading: !!tableLoading,
									}}
									alignColumnsCenter={alignColumnsCenter}
								/>
								<ConditionalRender show={!!tableAdditionalElement && !!showTableAdditionalElement}>
									{tableAdditionalElement}
								</ConditionalRender>
							</div>
						</GridItem>
					</>
				</GridContainer>
			)}
			onFalse={() => (
				<GridContainer justify="flex-start" alignContent={'center'}>
					<GridItem xs={12}>
						<CustomCircularProgress loading={loading} text={translate('loading')} size={48} />
					</GridItem>
				</GridContainer>
			)}
		/>
	);
};

export default AccountFilterTable;
